import React from 'react';
import useSWR from 'swr';
import ShadowRoot from './ShadowRootPolyfill';
import {
  RenderIntervalTracker,
  useIntervalTracker,
} from '@packages/systems/render';
import {encodeDoubleQuotes} from '@packages/systems/core/utils/TextUtils';
import {encodeCodeComponentStreamingMarkers} from '@packages/systems/render-atoms/ssr-utils';
import {
  buildCodeIslandPrerenderDataScript,
  type IRendererPublicWebflowContext,
} from './CodeIslandSharedUtils';

type SsrWebflowContext = Omit<IRendererPublicWebflowContext, 'functions'>;

export type CodeIslandSsrProps = {
  ssrRendererUrl: string;
  serializedProps: string;
  serializedSlots: string;
  componentId: string;
  tagStylesUrl?: string | null;
  nestedRootEnabled: boolean;
  prerenderEnabled?: boolean;
  webflowContext?: SsrWebflowContext;
};

type Response = {
  ok: boolean;
  version?: string;
  bundleVersion?: number;
  renderedHtml?: string;
  styles?: string;
  stylesheets?: string[];
  data?: Record<string, unknown>;
  err?: string;
};

type Data = {
  success: boolean;
  html: string;
  styles: string | null;
  stylesheets: string[];
  version: string;
  bundleVersion: number;
  prerenderData?: Record<string, unknown>;
};

const fetchData =
  (url: string, tracker: RenderIntervalTracker) =>
  async (): Promise<Response> => {
    const timer = tracker.startTimer('fetch_data');
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const data = await response.json();
      timer.finish({status: 'success'});
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      timer.finish({status: 'error'});
      return {ok: false, err: message};
    }
  };

function useFetchData({
  ssrRendererUrl,
  serializedProps,
  serializedSlots,
  componentId,
  prerenderEnabled = false,
  webflowContext,
}: {
  ssrRendererUrl: string;
  serializedProps: string;
  serializedSlots: string;
  componentId: string;
  prerenderEnabled?: boolean;
  webflowContext?: SsrWebflowContext;
}): Data {
  const tracker = useIntervalTracker('codeIslandSsr');
  const params = new URLSearchParams({
    props: serializedProps,
    slots: serializedSlots,
    prerender: prerenderEnabled ? 'true' : 'false',
    ...(webflowContext ? {context: JSON.stringify(webflowContext)} : {}),
  }).toString();

  const url = ssrRendererUrl
    ? `${ssrRendererUrl}${componentId}?${params}`
    : null;
  const fetcher = url ? fetchData(url, tracker) : null;
  const {data} = useSWR(url, fetcher, {
    suspense: true,
  });

  const success = Boolean(data?.ok);
  const error: string = data?.err || 'Unknown error';
  const html: string = data?.renderedHtml || '';
  const stylesheets: string[] = data?.stylesheets || [];
  const styles: string | null = data?.styles || null;
  const version: string = data?.version || 'unknown';
  const bundleVersion: number = data?.bundleVersion || -1;
  const prerenderData = success ? data?.data : undefined;

  if (!url) {
    console.error('[CodeIslandSsr] SSR renderer URL is missing.');
  } else if (!success) {
    console.error(
      `[CodeIslandSsr][version: ${version}][bundleVersion: ${bundleVersion}] Failed to fetch SSR data: %s`,
      error
    );
  } else if (!html) {
    console.error(
      `[CodeIslandSsr][version: ${version}][bundleVersion: ${bundleVersion}] Fetched HTML is empty.`
    );
  }

  return {
    success,
    // ================== IMPORTANT ==================
    // We encode the HTML with React streaming placeholders so they survive the
    // atoms SSR strip then decode them in render-atoms for correct hydration.
    html: encodeCodeComponentStreamingMarkers(html),
    styles,
    stylesheets,
    version,
    bundleVersion,
    prerenderData,
  };
}

/**
 * This component is used to render the SSR output of a code island,
 * fetching it from the provided URL and injects it into the DOM.
 */
export function CodeIslandSsr({
  componentId,
  ssrRendererUrl,
  serializedProps,
  serializedSlots,
  tagStylesUrl,
  nestedRootEnabled,
  prerenderEnabled,
  webflowContext,
}: CodeIslandSsrProps) {
  const {success, html, styles, stylesheets, prerenderData} = useFetchData({
    ssrRendererUrl,
    serializedProps,
    serializedSlots,
    componentId,
    prerenderEnabled,
    webflowContext,
  });

  if (!nestedRootEnabled) {
    // We keep this for backwards compatibility so we can render
    // existing published sites that don't have the nested root.
    // Note: styles in SSR are only supported with the nested root.
    return <ShadowRoot mode="open" dangerouslySetInnerHTML={{__html: html}} />;
  }

  // Build inner html
  let innerHtml = '';

  // Add <link> tags for stylesheets
  innerHtml += [...stylesheets, tagStylesUrl].reduce<string>(
    (links, href) =>
      href
        ? `${links}<link rel="stylesheet" type="text/css" href="${encodeDoubleQuotes(href)}"/>`
        : links,
    ''
  );

  // Add <style> tags
  innerHtml += styles || '';

  // Add server data <script> tag
  innerHtml += buildCodeIslandPrerenderDataScript(prerenderData);

  // Add data-root element
  innerHtml += `<div data-root="true" ${!success ? 'data-ssr-error="true" ' : ''}style="display:contents">${html}</div>`;

  return (
    <ShadowRoot mode="open" dangerouslySetInnerHTML={{__html: innerHtml}} />
  );
}
